import React, { useEffect, useRef, useState } from "react";

import Button from "../../Components/Button";
import CommonModal from "../../Components/Modal";
import {
  CADENCE_ORDER,
  CadenceKey,
} from "../../Pages/GuidancePlaybooks/constants";
import { useAppDispatch, useAppSelector } from "../../Store/Hooks";
import { updateGuidancePlaybookLinks } from "../../Store/Reducers/GuidanceSlice";
import {
  CADENCE_BADGE_STYLES,
  CADENCE_GENERIC_LABEL,
  CADENCE_ICON,
  resolveCadenceLabel,
} from "../../Utils/Guidance/guidanceCadence";
import {
  GUIDANCE_ROLES,
  GuidanceRole,
} from "../../Utils/Guidance/guidanceRole";
import { getAccessibleLinkKeysForRole } from "../../Utils/Guidance/guidanceRoleAccess";
import { toast } from "../../Utils/Helper/toast";

const MAX_LINKS = 3;

interface DraftAgendaItem {
  item: string;
  time: string;
}

interface DraftContent {
  cadenceTag: string;
  heading: string;
  time: string;
  nav: string;
  agenda: DraftAgendaItem[];
  what: string;
  lookfor: string;
  links: string[];
}

const emptyDraft: DraftContent = {
  cadenceTag: "",
  heading: "",
  time: "",
  nav: "",
  agenda: [],
  what: "",
  lookfor: "",
  links: [],
};

const parseAgendaFromCell = (
  raw: Array<Record<string, unknown>> | null | undefined
): DraftAgendaItem[] => {
  if (!raw?.length) return [];
  return raw
    .map(row => ({
      item: String(row.item ?? "").trim(),
      time: row.time != null ? String(row.time).trim() : "",
    }))
    .filter(row => row.item.length > 0);
};

const fieldClass = (isEditing: boolean) =>
  `w-full text-sm font-normal text-gray-900 border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-purple-200 focus:border-purple-300 ${
    !isEditing ? "bg-gray-50 text-gray-500 cursor-default" : "bg-white"
  }`;

/**
 * Admin > Playbook Content — edit any role × cadence playbook
 * (heading/time/nav/agenda/what/lookfor/links) with a live preview.
 */
const PlaybookContentTab: React.FC = () => {
  const dispatch = useAppDispatch();
  const { navLinks, playbookLinks, playbookLinksLoading, savingKey } =
    useAppSelector(state => state.guidance);

  const [role, setPersona] = useState<GuidanceRole>("ae");
  const [cadence, setCadence] = useState<CadenceKey>("daily");
  const [draft, setDraft] = useState<DraftContent>(emptyDraft);
  const [published, setPublished] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [hasUnpublishedChanges, setHasUnpublishedChanges] = useState(false);
  const [showPublishConfirm, setShowPublishConfirm] = useState(false);
  const draftBeforeEdit = useRef<DraftContent>(emptyDraft);

  const cell = playbookLinks.find(
    p => p.role === role && p.cadence === cadence
  );

  const buildDraftFromCell = (): DraftContent => {
    const allowed = new Set(getAccessibleLinkKeysForRole(role));
    return {
      cadenceTag: cell?.cadence_tag?.trim() || "",
      heading: cell?.heading || "",
      time: cell?.time || "",
      nav: cell?.nav || "",
      agenda: parseAgendaFromCell(cell?.agenda),
      what: (cell?.what || []).join("\n"),
      lookfor: (cell?.lookfor || []).join("\n"),
      links: (cell?.links ?? []).filter(key => allowed.has(key)),
    };
  };

  useEffect(() => {
    setDraft(buildDraftFromCell());
    setPublished(false);
    setIsEditing(false);
    setHasUnpublishedChanges(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [role, cadence, cell?.updated_at]);

  const linksRegistry = navLinks;
  const allowedLinkKeys = getAccessibleLinkKeysForRole(role);
  const allowedLinkKeySet = new Set(allowedLinkKeys);
  const activeLinks = linksRegistry.filter(
    l => l.active && allowedLinkKeySet.has(l.key)
  );
  const isPublishing = savingKey === `${role}:${cadence}`;
  const roleLabel = GUIDANCE_ROLES.find(p => p.key === role)?.label || role;

  const toggleLink = (key: string) => {
    if (!allowedLinkKeySet.has(key)) {
      toast.error("This route is not available for the selected role.");
      return;
    }
    setDraft(prev => {
      if (prev.links.includes(key)) {
        return { ...prev, links: prev.links.filter(l => l !== key) };
      }
      if (prev.links.length >= MAX_LINKS) return prev;
      return { ...prev, links: [...prev.links, key] };
    });
  };

  const whatLines = draft.what
    .split("\n")
    .map(s => s.trim())
    .filter(Boolean);
  const lookforLines = draft.lookfor
    .split("\n")
    .map(s => s.trim())
    .filter(Boolean);
  const agendaItems = draft.agenda
    .map(row => ({
      item: row.item.trim(),
      time: row.time.trim(),
    }))
    .filter(row => row.item.length > 0);

  const updateAgendaItem = (index: number, patch: Partial<DraftAgendaItem>) => {
    setDraft(prev => ({
      ...prev,
      agenda: prev.agenda.map((row, i) =>
        i === index ? { ...row, ...patch } : row
      ),
    }));
  };

  const addAgendaItem = () => {
    setDraft(prev => ({
      ...prev,
      agenda: [...prev.agenda, { item: "", time: "" }],
    }));
  };

  const removeAgendaItem = (index: number) => {
    setDraft(prev => ({
      ...prev,
      agenda: prev.agenda.filter((_, i) => i !== index),
    }));
  };

  const handleStartEdit = () => {
    draftBeforeEdit.current = draft;
    setIsEditing(true);
  };

  const promptClickEdit = () => {
    if (!isEditing) {
      toast.info("Click the Edit button to make changes.");
    }
  };

  const handleSave = () => {
    setIsEditing(false);
    setHasUnpublishedChanges(true);
  };

  const handleCancel = () => {
    setDraft(draftBeforeEdit.current);
    setIsEditing(false);
  };

  const handlePublish = () => {
    const allowedLinks = draft.links.filter(key => allowedLinkKeySet.has(key));
    if (allowedLinks.length !== draft.links.length) {
      toast.error(
        "One or more selected links are not available for this role. Remove them before publishing."
      );
      return;
    }
    dispatch(
      updateGuidancePlaybookLinks({
        role,
        cadence,
        cadence_tag: draft.cadenceTag.trim() || null,
        heading: draft.heading.trim(),
        time: draft.time.trim(),
        nav: draft.nav.trim() || null,
        what: whatLines,
        lookfor: lookforLines,
        links: allowedLinks,
        agenda:
          agendaItems.length > 0
            ? agendaItems.map((row, idx) => ({
                n: idx + 1,
                item: row.item,
                ...(row.time ? { time: row.time } : {}),
              }))
            : null,
      })
    )
      .unwrap()
      .then(() => {
        setShowPublishConfirm(false);
        setHasUnpublishedChanges(false);
        setPublished(true);
        setTimeout(() => setPublished(false), 1500);
      })
      .catch(() => {
        // Errors surface via playbookLinksError; leave modal open to retry.
      });
  };

  const style = CADENCE_BADGE_STYLES[cadence];
  const cadenceLabel = resolveCadenceLabel(draft.cadenceTag, cadence);
  const personaShortLabel = roleLabel.toUpperCase();

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">
            Playbook Content Editor
          </h3>
          <p className="mt-1 text-sm text-gray-500 max-w-2xl">
            Read-only until you click Edit. Save keeps edits local — Publish
            pushes them to the Hub, home widget, and drawer.
          </p>
        </div>
        {hasUnpublishedChanges && (
          <span className="shrink-0 text-xs font-semibold text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-3 py-1">
            Saved locally — not yet live
          </span>
        )}
      </div>

      {playbookLinksLoading && (
        <div className="rounded-xl border border-gray-200 bg-gray-50 px-5 py-4 text-sm text-gray-500">
          Loading playbook content…
        </div>
      )}
      {!playbookLinksLoading && !cell && (
        <div className="rounded-xl border border-amber-200 bg-amber-50 px-5 py-4 text-sm text-amber-800">
          No API cell for this role × cadence yet. Seed or create it before
          editing.
        </div>
      )}

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-5 items-start">
        <section className="rounded-xl border border-gray-200 bg-white overflow-hidden">
          <div className="flex flex-wrap items-end justify-between gap-3 px-5 py-4 border-b border-gray-100 bg-gray-50/80">
            <div className="flex flex-wrap gap-3">
              <label className="flex flex-col gap-1.5 text-[11px] font-semibold text-gray-500 uppercase tracking-wide">
                Role
                <select
                  value={role}
                  onChange={e => setPersona(e.target.value as GuidanceRole)}
                  className="min-w-[140px] text-sm font-normal normal-case tracking-normal text-gray-900 border border-gray-200 rounded-lg px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-purple-200 focus:border-purple-300"
                >
                  {GUIDANCE_ROLES.map(p => (
                    <option key={p.key} value={p.key}>
                      {p.label}
                    </option>
                  ))}
                </select>
              </label>
              <label className="flex flex-col gap-1.5 text-[11px] font-semibold text-gray-500 uppercase tracking-wide">
                Cadence
                <select
                  value={cadence}
                  onChange={e => setCadence(e.target.value as CadenceKey)}
                  className="min-w-[120px] text-sm font-normal normal-case tracking-normal text-gray-900 border border-gray-200 rounded-lg px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-purple-200 focus:border-purple-300"
                >
                  {CADENCE_ORDER.map(c => (
                    <option key={c} value={c}>
                      {CADENCE_GENERIC_LABEL[c]}
                    </option>
                  ))}
                </select>
              </label>
              <label
                className="flex flex-col gap-1.5 text-[11px] font-semibold text-gray-500 uppercase tracking-wide"
                title={`Shown instead of "${CADENCE_GENERIC_LABEL[cadence]}" in the Playbooks Hub`}
              >
                User tag
                <input
                  type="text"
                  value={draft.cadenceTag}
                  readOnly={!isEditing}
                  placeholder="Optional"
                  onMouseDown={promptClickEdit}
                  onChange={e =>
                    setDraft({ ...draft, cadenceTag: e.target.value })
                  }
                  className={`w-36 ${fieldClass(isEditing)}`}
                />
              </label>
            </div>
            {!isEditing ? (
              <button
                type="button"
                onClick={handleStartEdit}
                className="shrink-0 text-sm font-semibold text-white bg-[#974ae4] hover:bg-[#8538d4] rounded-lg px-4 py-2 transition-colors"
              >
                Edit
              </button>
            ) : (
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={handleSave}
                  className="text-sm font-semibold text-white bg-[#974ae4] hover:bg-[#8538d4] rounded-lg px-4 py-2 transition-colors"
                >
                  Save Changes
                </button>
                <button
                  type="button"
                  onClick={handleCancel}
                  className="text-sm font-semibold text-gray-600 border border-gray-200 bg-white rounded-lg px-4 py-2 hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
              </div>
            )}
          </div>

          <div className="px-5 py-5 space-y-4">
            <label className="flex flex-col gap-1.5 text-[11px] font-semibold text-gray-500 uppercase tracking-wide">
              Heading
              <input
                type="text"
                value={draft.heading}
                readOnly={!isEditing}
                onMouseDown={promptClickEdit}
                onChange={e => setDraft({ ...draft, heading: e.target.value })}
                className={fieldClass(isEditing)}
              />
            </label>
            <label className="flex flex-col gap-1.5 text-[11px] font-semibold text-gray-500 uppercase tracking-wide">
              Time estimate
              <input
                type="text"
                value={draft.time}
                readOnly={!isEditing}
                placeholder="e.g. 15 min"
                onMouseDown={promptClickEdit}
                onChange={e => setDraft({ ...draft, time: e.target.value })}
                className={fieldClass(isEditing)}
              />
            </label>
            <label className="flex flex-col gap-1.5 text-[11px] font-semibold text-gray-500 uppercase tracking-wide">
              <span>
                Nav path{" "}
                <span className="font-normal normal-case tracking-normal text-gray-400">
                  (breadcrumb shown on Hub cards)
                </span>
              </span>
              <input
                type="text"
                value={draft.nav}
                readOnly={!isEditing}
                placeholder="e.g. My Accounts · Relationships"
                onMouseDown={promptClickEdit}
                onChange={e => setDraft({ ...draft, nav: e.target.value })}
                className={fieldClass(isEditing)}
              />
            </label>

            <div>
              <div className="flex items-baseline justify-between gap-2 mb-2">
                <div className="text-[11px] font-semibold text-gray-500 uppercase tracking-wide">
                  Agenda{" "}
                  <span className="font-normal normal-case tracking-normal text-gray-400">
                    (optional — shown in drawer for call cadences)
                  </span>
                </div>
                {isEditing && (
                  <button
                    type="button"
                    onClick={addAgendaItem}
                    className="text-xs font-semibold text-[#974ae4] hover:text-[#8538d4]"
                  >
                    + Add item
                  </button>
                )}
              </div>
              {draft.agenda.length === 0 ? (
                <div
                  onMouseDown={promptClickEdit}
                  className={`rounded-lg border border-dashed border-gray-200 px-3 py-3 text-xs text-gray-400 ${
                    !isEditing ? "cursor-pointer bg-gray-50" : "bg-white"
                  }`}
                >
                  {isEditing
                    ? "No agenda items yet. Use + Add item for timed call steps."
                    : "No agenda for this playbook."}
                </div>
              ) : (
                <div className="space-y-2">
                  {draft.agenda.map((row, idx) => (
                    <div
                      key={idx}
                      className="flex items-start gap-2 rounded-lg border border-gray-200 bg-white p-2.5"
                    >
                      <span className="mt-2 w-5 shrink-0 text-xs font-semibold text-gray-400 text-right">
                        {idx + 1}.
                      </span>
                      <div className="min-w-0 flex-1 space-y-2">
                        <input
                          type="text"
                          value={row.item}
                          readOnly={!isEditing}
                          placeholder="Agenda item"
                          onMouseDown={promptClickEdit}
                          onChange={e =>
                            updateAgendaItem(idx, { item: e.target.value })
                          }
                          className={fieldClass(isEditing)}
                        />
                        <input
                          type="text"
                          value={row.time}
                          readOnly={!isEditing}
                          placeholder="Time (e.g. 5 min)"
                          onMouseDown={promptClickEdit}
                          onChange={e =>
                            updateAgendaItem(idx, { time: e.target.value })
                          }
                          className={`w-36 ${fieldClass(isEditing)}`}
                        />
                      </div>
                      {isEditing && (
                        <button
                          type="button"
                          onClick={() => removeAgendaItem(idx)}
                          className="mt-1.5 shrink-0 text-xs font-semibold text-gray-400 hover:text-red-600 px-1.5 py-1"
                          aria-label={`Remove agenda item ${idx + 1}`}
                        >
                          Remove
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            <label className="flex flex-col gap-1.5 text-[11px] font-semibold text-gray-500 uppercase tracking-wide">
              What to do{" "}
              <span className="font-normal normal-case tracking-normal text-gray-400">
                (one bullet per line)
              </span>
              <textarea
                rows={5}
                value={draft.what}
                readOnly={!isEditing}
                onMouseDown={promptClickEdit}
                onChange={e => setDraft({ ...draft, what: e.target.value })}
                className={`${fieldClass(isEditing)} resize-y`}
              />
            </label>
            <label className="flex flex-col gap-1.5 text-[11px] font-semibold text-gray-500 uppercase tracking-wide">
              What to look for{" "}
              <span className="font-normal normal-case tracking-normal text-gray-400">
                (one bullet per line)
              </span>
              <textarea
                rows={5}
                value={draft.lookfor}
                readOnly={!isEditing}
                onMouseDown={promptClickEdit}
                onChange={e => setDraft({ ...draft, lookfor: e.target.value })}
                className={`${fieldClass(isEditing)} resize-y`}
              />
            </label>

            <div>
              <div className="flex items-baseline justify-between gap-2 mb-2">
                <div className="text-[11px] font-semibold text-gray-500 uppercase tracking-wide">
                  Nav links{" "}
                  <span className="font-normal normal-case tracking-normal text-gray-400">
                    (max {MAX_LINKS} — routes {roleLabel} can access)
                  </span>
                </div>
                <span className="text-xs text-gray-400">
                  {draft.links.length}/{MAX_LINKS}
                </span>
              </div>
              {activeLinks.length === 0 ? (
                <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2.5 text-xs text-amber-800">
                  No routes are available for this role.
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {activeLinks.map(link => {
                    const checked = draft.links.includes(link.key);
                    const atLimit = !checked && draft.links.length >= MAX_LINKS;
                    const disabled = !isEditing || atLimit;
                    return (
                      <label
                        key={link.key}
                        onClick={e => {
                          if (!isEditing) {
                            e.preventDefault();
                            promptClickEdit();
                          }
                        }}
                        className={`flex items-start gap-2.5 rounded-lg border px-3 py-2.5 text-sm transition-colors ${
                          checked
                            ? "border-purple-200 bg-purple-50/60"
                            : "border-gray-200 bg-white"
                        } ${
                          !isEditing
                            ? "cursor-pointer"
                            : disabled
                              ? "opacity-60 cursor-not-allowed"
                              : "cursor-pointer hover:border-purple-200"
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => toggleLink(link.key)}
                          disabled={disabled}
                          className={`mt-0.5 rounded border-gray-300 text-[#974ae4] focus:ring-purple-200 ${
                            !isEditing ? "pointer-events-none" : ""
                          }`}
                        />
                        <span className="min-w-0">
                          <span className="block font-medium text-gray-900 leading-snug">
                            {link.label}
                          </span>
                          <span className="block font-mono text-[11px] text-gray-400 truncate mt-0.5">
                            {link.path}
                          </span>
                        </span>
                      </label>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </section>

        <aside className="xl:sticky xl:top-4 space-y-4 min-w-0">
          <div className="rounded-xl border border-gray-200 bg-white overflow-hidden shadow-sm min-w-0">
            <div className="px-5 py-3 border-b border-gray-100 bg-gray-50/80">
              <div className="text-[11px] font-semibold tracking-widest text-gray-400 uppercase">
                Live preview
              </div>
              <p className="mt-0.5 text-xs text-gray-500">
                Matches the Guide Drawer for {roleLabel} ·{" "}
                {CADENCE_GENERIC_LABEL[cadence]}
              </p>
            </div>

            {/* Drawer chrome — no Today badge, Float, or close */}
            <div className="border-b border-gray-100 px-5 py-4">
              <div className="flex items-start gap-2">
                <span
                  role="img"
                  aria-hidden="true"
                  className="text-lg leading-none shrink-0 mt-0.5"
                >
                  🧭
                </span>
                <div className="min-w-0">
                  <div className="text-[10px] font-bold uppercase tracking-wide text-[#2563eb]">
                    Playbooks Hub
                  </div>
                  <div className="text-sm font-bold text-[#0f1929]">
                    {cadenceLabel} Playbook
                  </div>
                  <div className="text-[11px] font-normal text-[#6b7a8d] mt-px">
                    {personaShortLabel}
                    {draft.time.trim() ? ` · ${draft.time.trim()}` : ""}
                  </div>
                </div>
              </div>
            </div>

            <div className="px-5 py-4 min-w-0 overflow-hidden">
              <div className="mb-4 min-w-0">
                <div
                  className="text-[11px] font-bold uppercase tracking-wide mb-1.5 flex items-center gap-1.5"
                  style={{ color: style.text }}
                >
                  <span>{CADENCE_ICON[cadence]}</span>
                  <span className="min-w-0 break-words">
                    Cadence: {cadenceLabel}
                  </span>
                </div>
                <h3 className="font-bold text-sm text-gray-900 break-words">
                  {draft.heading.trim() || "—"}
                </h3>
                {draft.nav.trim() && (
                  <p className="text-xs text-gray-400 italic mt-1 break-words">
                    ↗ {draft.nav.trim()}
                  </p>
                )}
              </div>

              {agendaItems.length > 0 && (
                <div className="mb-4 min-w-0">
                  <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                    Agenda
                  </h4>
                  <ol className="space-y-1.5">
                    {agendaItems.map((item, idx) => (
                      <li
                        key={`${idx}-${item.item}`}
                        className="text-sm text-gray-700 flex gap-2 min-w-0"
                      >
                        <span className="text-gray-400 shrink-0">
                          {idx + 1}.
                        </span>
                        <span className="min-w-0 flex-1 break-words">
                          {item.item}
                        </span>
                        {item.time && (
                          <span className="text-xs text-gray-400 shrink-0">
                            {item.time}
                          </span>
                        )}
                      </li>
                    ))}
                  </ol>
                </div>
              )}

              {whatLines.length > 0 && (
                <div className="mb-4 min-w-0">
                  <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                    What to do
                  </h4>
                  <ul className="space-y-1.5">
                    {whatLines.map((line, idx) => (
                      <li
                        key={idx}
                        className="text-sm text-gray-700 flex gap-1.5 min-w-0"
                      >
                        <span className="text-[#2563eb] font-bold shrink-0">
                          →
                        </span>
                        <span className="min-w-0 break-words">{line}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {lookforLines.length > 0 && (
                <div className="mb-4 min-w-0">
                  <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                    What to look for
                  </h4>
                  <ul className="space-y-1.5">
                    {lookforLines.map((line, idx) => (
                      <li
                        key={idx}
                        className="text-sm text-gray-700 flex gap-1.5 min-w-0"
                      >
                        <span className="text-[#2563eb] font-bold shrink-0">
                          →
                        </span>
                        <span className="min-w-0 break-words">{line}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              <div className="mb-1">
                <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                  Jump to
                </h4>
                {draft.links.length > 0 ? (
                  <div className="flex flex-wrap gap-2">
                    {draft.links.map(key => {
                      const label =
                        linksRegistry.find(l => l.key === key)?.label || key;
                      return (
                        <span
                          key={key}
                          className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-[7px] border border-[#bfcefe] bg-[#eff4ff] text-[#2563eb]"
                        >
                          ↗ {label}
                        </span>
                      );
                    })}
                  </div>
                ) : (
                  <p className="text-xs text-gray-400">No nav links selected</p>
                )}
              </div>
            </div>
          </div>

          <div className="rounded-xl border border-gray-200 bg-white px-5 py-4 flex flex-wrap items-center gap-3">
            <Button
              variant="primary"
              onClick={() => setShowPublishConfirm(true)}
              disabled={!hasUnpublishedChanges}
              loading={isPublishing}
            >
              {published ? "✓ Published" : "Publish"}
            </Button>
            {!hasUnpublishedChanges && !published && (
              <span className="text-xs text-gray-400">
                Make and save edits to enable Publish
              </span>
            )}
            {published && (
              <span className="text-xs font-semibold text-green-700">
                Live for this org
              </span>
            )}
          </div>
        </aside>
      </div>

      <CommonModal
        isOpen={showPublishConfirm}
        toggle={() => setShowPublishConfirm(false)}
        title="Publish playbook content?"
        size="sm"
        footer={
          <div className="flex justify-end gap-2">
            <Button variant="gray" onClick={() => setShowPublishConfirm(false)}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handlePublish}
              loading={isPublishing}
            >
              Publish
            </Button>
          </div>
        }
      >
        <p className="text-sm text-gray-600 leading-relaxed">
          This pushes the saved {roleLabel} / {CADENCE_GENERIC_LABEL[cadence]}{" "}
          content live to the Playbooks Hub, the home widget, and the Guide
          Drawer for every user in this org — immediately. To change it later,
          edit and publish again.
        </p>
      </CommonModal>
    </div>
  );
};

export default PlaybookContentTab;
